import React from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { gql, useQuery, useMutation } from "@apollo/client";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSnackbar } from "notistack";

import {
  Alert,
  Box,
  List,
  ListItem,
  CircularProgress,
  Fab,
} from "@mui/material";
import { Plus as CreateIcon } from "mdi-material-ui";

import Layout from "components/common/Layout";

import CreateArticleDialog from "./CreateArticleDialog";
import ArticlesListItem from "./ArticlesListItem";

const QUERY = gql`
  query ($first: Int, $after: String, $type: String!) {
    articles(first: $first, after: $after, type: $type) {
      pageInfo {
        hasNextPage
        endCursor
      }

      edges {
        cursor
        node {
          id

          ...ArticlesListItem_Article
        }
      }
    }
  }

  ${ArticlesListItem.fragments.article}
`;

const MUTATION = gql`
  mutation ($articleIds: [ID]!, $type: String!) {
    reorderArticles: ReorderArticles(articleIds: $articleIds) {
      articles(type: $type) {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`;

export default function ArticlesList({ type }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { data, loading, fetchMore } = useQuery(QUERY, {
    variables: { first: 20, type },
    fetchPolicy: "network-only",
  });

  const [reorderArticles] = useMutation(MUTATION);

  const loadNextOnDragEnd = React.useRef();
  const onDragEnd = React.useCallback(
    async (result) => {
      // dropped outside the list
      if (!result.destination) return;

      const snackbarKey = enqueueSnackbar("Enregistrement en cours …", {
        persist: true,
        action: <CircularProgress size={30} color="inherit" />,
      });

      const reorderedEdges = Array.from(data.articles.edges);
      const [removed] = reorderedEdges.splice(result.source.index, 1);
      reorderedEdges.splice(result.destination.index, 0, removed);

      await reorderArticles({
        variables: {
          articleIds: reorderedEdges.map(({ node }) => node.id),
          type,
        },
        optimisticResponse: {
          reorderArticles: {
            articles: {
              edges: reorderedEdges,
              __typename: "ArticleConnection",
            },
            __typename: "ReorderArticles",
          },
        },
        update: (store, { data: { reorderArticles } }) => {
          const getEdge = (articleId) =>
            data.articles.edges.find(({ node }) => node.id === articleId);

          const edges = reorderArticles.articles.edges
            .map(({ node }) => getEdge(node.id))
            .filter(Boolean);

          store.writeQuery({
            query: QUERY,
            variables: { type },
            data: {
              ...data,
              articles: {
                ...data.articles,
                edges,
              },
            },
          });
        },
      });

      closeSnackbar(snackbarKey);

      if (loadNextOnDragEnd.current) {
        loadNextOnDragEnd.current();
        loadNextOnDragEnd.current = null;
      }
    },
    [data, type, reorderArticles, enqueueSnackbar, closeSnackbar]
  );

  const endCursor = data?.articles.pageInfo.endCursor;
  const loadNext = React.useCallback(
    () => fetchMore({ variables: { after: endCursor } }),
    [fetchMore, endCursor]
  );

  if (loading) return <Layout.Loading />;

  return (
    <>
      <CreateArticleDialog type={type}>
        {({ open }) => (
          <Box
            sx={{
              position: "absolute",
              zIndex: 1,
              top: (t) => t.spacing(2),
              right: (t) => t.spacing(3),
            }}
          >
            <Fab
              size="small"
              color="primary"
              onClick={open}
              sx={{ position: "fixed", transform: "translateX(-100%)" }}
            >
              <CreateIcon />
            </Fab>
          </Box>
        )}
      </CreateArticleDialog>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => {
            const disableInfiniteScroll =
              snapshot.draggingOverWith || snapshot.draggingFromThisWith;

            return (
              <List dense {...provided.droppableProps} ref={provided.innerRef}>
                <InfiniteScroll
                  scrollableTarget="Layout-Main"
                  dataLength={data?.articles.edges.length ?? 0}
                  hasMore={data?.articles.pageInfo.hasNextPage ?? loading}
                  next={
                    disableInfiniteScroll
                      ? () => (loadNextOnDragEnd.current = loadNext)
                      : loadNext
                  }
                  loader={
                    disableInfiniteScroll ? (
                      <ListItem component={Alert} severity="info">
                        Impossible de charger la suite pendant un drag’n’drop.
                      </ListItem>
                    ) : (
                      <Layout.Loading padded />
                    )
                  }
                >
                  {data?.articles.edges.map(({ node }, index) => (
                    <ArticlesListItem
                      key={node.id}
                      article={node}
                      index={index}
                    />
                  ))}
                  {provided.placeholder}
                </InfiniteScroll>
              </List>
            );
          }}
        </Droppable>
      </DragDropContext>
    </>
  );
}
